// DOCS: https://www.algolia.com/doc/ui-libraries/autocomplete/guides/creating-a-renderer/
import type { AutocompleteState } from '@algolia/autocomplete-core';
import { createAutocomplete } from '@algolia/autocomplete-core';
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia';
import { DialogContent, DialogTitle } from '@mui/material';
import algoliasearch from 'algoliasearch/lite';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { type InternalDocSearchHit, type StoredDocSearchHit } from 'common';
import { noop } from 'modules/utils';
import type { FooterTranslations } from './Footer';
import { Hit, getURLByType } from './Hit';
import { ScreenState, ScreenStateTranslations } from './ScreenState';
import { SearchProps } from './Search';
import { SearchBox, SearchBoxTranslations } from './SearchBox';
import { createStoredSearches } from './storedSearches';
import { groupByCollectionName, identity, isModifierEvent } from './utils';

// TODO: use tags to filter results by collectionName when user clicks Chip
// https://www.algolia.com/doc/ui-libraries/autocomplete/guides/filtering-results/#adding-tags
// TODO: pass initial tag as filter when search button clicked in x grid view
// TODO: add predefined items (FAQ's, submit claim, etc.)

export type ModalTranslations = Partial<{
  searchBox: SearchBoxTranslations;
  footer: FooterTranslations;
}> &
  ScreenStateTranslations;

export type SearchModalProps = SearchProps & {
  // initialScrollY: number;
  onClose?: () => void;
  translations?: ModalTranslations;
};

export function SearchModal({
  appId,
  apiKey,
  indexName,
  indexTitle,
  searchParameters,
  hitComponent = Hit,
  resultsFooterComponent = () => null,
  disableUserPersonalization = false,
  placeholder = 'Search records',
  // navigator,
  // initialQuery = '',
  translations = {},
  getMissingResultsUrl,
  onClose = noop,
  onSelect,
  transformItems = identity,
}: SearchModalProps) {
  const {
    footer: footerTranslations,
    searchBox: searchBoxTranslations,
    ...screenStateTranslations
  } = translations;
  const navigate = useNavigate();
  const [state, setState] = useState<AutocompleteState<InternalDocSearchHit>>({
    query: '',
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    activeItemId: null,
    status: 'idle',
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const formElementRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // const snippetLength = React.useRef<number>(10);

  const searchClient = useMemo(() => algoliasearch(appId, apiKey), [appId, apiKey]);

  const favoriteSearches = useRef(
    createStoredSearches<StoredDocSearchHit>({
      key: `__FAVORITE_SEARCHES__${indexName}`,
      limit: 10,
    })
  ).current;
  const recentSearches = useRef(
    createStoredSearches<StoredDocSearchHit>({
      key: `__RECENT_SEARCHES__${indexName}`,
      limit: favoriteSearches.getAll().length === 0 ? 7 : 4,
    })
  ).current;

  const saveRecentSearch = useCallback(
    (item: InternalDocSearchHit) => {
      if (disableUserPersonalization) {
        return;
      }

      // Don't store `content` record, but their parent if available.
      const search = item.type === 'content' ? item.__docsearch_parent : item;

      // Save the recent search only if it's not favorited.
      if (
        search &&
        favoriteSearches.getAll().findIndex((x) => x.objectID === search.objectID) === -1
      ) {
        recentSearches.add(search);
      }
    },
    [favoriteSearches, recentSearches, disableUserPersonalization]
  );

  const autocomplete = useMemo(
    () =>
      createAutocomplete<
        InternalDocSearchHit,
        React.FormEvent<HTMLFormElement>,
        React.MouseEvent,
        React.KeyboardEvent
      >({
        id: 'search-autocomplete',
        defaultActiveItemId: 0,
        placeholder,
        openOnFocus: true,
        initialState: {
          query: '', // initialQuery,
          context: {
            searchSuggestions: [],
          },
        },
        // navigator, // default navigator: https://www.algolia.com/doc/ui-libraries/autocomplete/core-concepts/keyboard-navigation/
        navigator: {
          navigate({ itemUrl }) {
            !onSelect && itemUrl && navigate(itemUrl);
          },
          navigateNewTab({ itemUrl }) {
            if (!itemUrl) return;
            const windowReference = window.open(itemUrl, '_blank', 'noopener');

            if (windowReference) {
              windowReference.focus();
            }
          },
          navigateNewWindow({ itemUrl }) {
            if (!itemUrl) return;
            window.open(itemUrl, '_blank', 'noopener');
          },
        },
        onStateChange(props) {
          setState(props.state);
        },
        // plugins: [predefinedItemsPlugin],
        // DOCS IMPLEMENTATION
        // @ts-ignore // TODO: fix type
        getSources({ query, state: sourcesState, setContext, setStatus }) {
          if (!query) {
            if (disableUserPersonalization) {
              return [];
            }

            return [
              {
                sourceId: 'recentSearches',
                onSelect({ item, event }) {
                  saveRecentSearch(item);

                  if (!isModifierEvent(event)) {
                    onClose();
                  }
                },
                getItemUrl({ item, ...rest }) {
                  // TODO: getItemUrl func (use func from below)
                  return getURLByType(item);
                  // item.__autocomplete_indexName
                  // but need to use "type" when indexing in algolia b/c could be in suggestions index

                  // @ts-ignore
                  // if (item.__autocomplete_indexName) return ''; // TODO: fix
                  // return item.url;
                },
                getItems() {
                  return recentSearches.getAll();
                },
              },
              {
                sourceId: 'favoriteSearches',
                onSelect({ item, event }) {
                  saveRecentSearch(item);

                  if (!isModifierEvent(event)) {
                    onClose();
                  }
                },
                getItemUrl({ item }) {
                  return getURLByType(item);
                  // return item.url;
                },
                getItems() {
                  return favoriteSearches.getAll();
                },
              },
            ];
          }

          return [
            {
              sourceId: indexName,
              title: indexTitle,
              distinct: 1,
              onSelect(props) {
                const { item, event } = props;
                saveRecentSearch(item);
                // console.log('ON SELECT ITEM: ', item);
                if (onSelect) {
                  event.stopPropagation();
                  onSelect(item);
                }

                if (!isModifierEvent(event)) {
                  onClose();
                }
              },
              getItemInputValue({ item }) {
                // @ts-ignore
                return item.query;
              },
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName,
                      query,
                      params: {
                        hitsPerPage: 10,
                        highlightPreTag: '<mark>',
                        highlightPostTag: '</mark>',
                        ...searchParameters,
                      },
                    },
                  ],
                });
              },
              getItemUrl({ item }) {
                // called when item is hovered
                return getURLByType(item);
                // return item.url;
              },

              // templates: {
              //   header() {
              //     return 'Test title';
              //   },
              // },
            },
          ];
        },
        // @ts-ignore
        reshape(params: {
          sources: any[];
          sourcesBySourceId: Record<string, any>;
          state: AutocompleteState<InternalDocSearchHit>;
        }) {
          const { recentSearches, ...rest } = params.sourcesBySourceId;
          const searchSource = params.sourcesBySourceId[indexName];

          if (!searchSource) return [Object.values(rest)];

          return [groupByCollectionName(searchSource)]; //  Object.values(rest)
          // including Object.values(rest) results in duplicates (add and use de-dup func ??)

          // const items = searchSource.getItems();
          // const groupByResult = groupByOld(items, (item: any) => item.type, 4);
          // return [removeDuplicates(recentSearches), Object.values(rest)];

          // multiple reshape functions example:
          // return [
          //   limitSuggestions(removeDuplicates(recentSearchesPlugin, querySuggestionsPlugin)),
          //   Object.values(rest),
          // ];
        },

        // DOCSEARCH IMPLEMENTATION
        // https://github.com/algolia/docsearch/blob/main/packages/docsearch-react/src/DocSearchModal.tsx
      }),
    [
      indexName,
      indexTitle,
      searchParameters,
      searchClient,
      onClose,
      recentSearches,
      favoriteSearches,
      saveRecentSearch,
      // initialQuery,
      placeholder,
      // navigator,
      // transformItems,
      disableUserPersonalization,
      navigate,
      onSelect,
    ]
  );

  // TODO: add stalled state indicator: https://www.algolia.com/doc/ui-libraries/autocomplete/guides/creating-a-renderer/#reacting-to-the-network
  return (
    <div ref={containerRef} className='aa-Autocomplete' {...autocomplete.getRootProps({})}>
      <div ref={modalRef}>
        {/* className='DocSearch-Modal' */}
        <DialogTitle className='DocSearch-SearchBar' ref={formElementRef}>
          <SearchBox
            {...autocomplete}
            state={state}
            autoFocus={true} // {initialQuery.length === 0}
            inputRef={inputRef}
            // isFromSelection={
            //   Boolean(initialQuery) &&
            //   initialQuery === initialQueryFromSelection
            // }
            // translations={searchBoxTranslations}
            onClose={onClose}
          />
        </DialogTitle>
        <DialogContent
          dividers
          className='DocSearch-Dropdown'
          ref={dropdownRef}
          sx={{
            py: '0 !important',
          }}
          // sx={{
          //   maxHeight:
          //     'calc(var(--docsearch-vh, 1vh) * 100) - var(--docsearch-searchbox-height) - var(--docsearch-spacing) - var(--docsearch-footer-height)',
          // }}
        >
          <ScreenState
            {...autocomplete}
            indexName={indexName}
            state={state}
            hitComponent={hitComponent}
            resultsFooterComponent={resultsFooterComponent}
            disableUserPersonalization={disableUserPersonalization}
            recentSearches={recentSearches}
            favoriteSearches={favoriteSearches}
            inputRef={inputRef}
            translations={screenStateTranslations}
            getMissingResultsUrl={getMissingResultsUrl}
            onItemClick={(item, event) => {
              console.log('ON ITEM CLICK: ', item);
              saveRecentSearch(item);
              if (onSelect) {
                event.stopPropagation();
                onSelect(item);
              }
              if (!isModifierEvent(event)) {
                onClose();
              }
            }}
          />
        </DialogContent>
      </div>
    </div>
  );
}
